import { useState } from 'react';
import { useReduxState } from 'hooks';
import { localeLanguageCodes, localeNames } from 'shared-locales';
import { FlagIcon } from 'components/common';
import { Icon } from 'components/common/icons';

import type { CompetencyProfile } from 'shared-types';

interface Props {
  competencyProfile: Pick<
    CompetencyProfile,
    'id' | 'description' | 'descriptionLocales' | 'title' | 'titleLocales'
  >;
}

const CompetencyProfileListItem = ({ competencyProfile }: Props) => {
  const { messages, theme } = useReduxState();

  const [selectedLocale, setSelectedLocale] = useState('default');

  const descriptionLocales = Object.keys(
    competencyProfile.descriptionLocales || {}
  );
  const titleLocales = Object.keys(competencyProfile.titleLocales || {});

  const translatedLocales = [
    ...titleLocales.filter((locale) => competencyProfile.titleLocales[locale]),
    ...descriptionLocales.filter(
      (locale) =>
        competencyProfile.descriptionLocales[locale] &&
        !titleLocales.includes(locale)
    )
  ];

  return (
    <div className="profile">
      <div className="title">
        {selectedLocale !== 'default' &&
        competencyProfile.titleLocales?.[selectedLocale]
          ? competencyProfile.titleLocales?.[selectedLocale]
          : competencyProfile.title}
      </div>

      {competencyProfile.description ? (
        <div className="description">
          {selectedLocale !== 'default' &&
          competencyProfile.descriptionLocales?.[selectedLocale]
            ? competencyProfile.descriptionLocales?.[selectedLocale]
            : competencyProfile.description}
        </div>
      ) : null}

      {translatedLocales.length ? (
        <div className="item">
          <div className="item-label">
            {`${messages.settingsLanguageVariants}:`}
          </div>
          <div className="translations">
            <button
              className={`translation ${
                selectedLocale === 'default' ? 'active' : ''
              }`}
              aria-label={messages.commonDefault}
              data-tooltip={messages.commonDefault}
              onClick={() => setSelectedLocale('default')}
            >
              <Icon icon="globe-americas" style={{ padding: '0 2px' }} />
            </button>

            {translatedLocales.map((locale) => (
              <button
                className={`translation ${
                  selectedLocale === locale ? 'active' : ''
                }`}
                key={locale}
                aria-label={localeNames[locale]}
                data-tooltip={localeNames[locale]}
                onClick={() => setSelectedLocale(locale)}
              >
                <FlagIcon
                  lang={localeLanguageCodes[locale]}
                  style={{ marginTop: -2 }}
                />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .profile {
          min-width: 300px;
          padding: 5px 0;
        }
        .title {
          display: flex;
          flex-wrap: wrap;
          font-size: 1.5rem;
          font-weight: 700;
          gap: 8px;
          max-width: 600px;
        }
        .description {
          display: flex;
          flex-wrap: wrap;
          font-size: 1.4rem;
          font-weight: 400;
          gap: 5px;
          line-height: 1.4;
          margin-top: 5px;
          max-width: 600px;
        }
        .item {
          align-items: center;
          display: flex;
          gap: 15px;
          margin-top: 12px;
        }
        .item-label {
          font-size: 1.4rem;
          font-weight: 600px;
        }
        .translations {
          display: flex;
          gap: 5px;
        }
        .translation {
          background-color: transparent;
          border: 0;
          border-radius: 5px;
          cursor: pointer;
          outline: 0;
          font-size: 1.4rem;
          padding: 3px 5px;
        }
        .translation.active {
          background-color: ${theme.lightGreyDark};
        }
      `}</style>
    </div>
  );
};

export default CompetencyProfileListItem;
